import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, Clock, TrendingUp, Filter, RefreshCw, AlertCircle, Trophy, BarChart3 } from 'lucide-react'
import sportsDataService from '../services/sportsDataService'
import advancedAnalytics from '../services/advancedAnalytics'

const Games = () => {
  const [games, setGames] = useState([])
  const [recentGames, setRecentGames] = useState([])
  const [upcomingGames, setUpcomingGames] = useState([])
  const [odds, setOdds] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedTab, setSelectedTab] = useState('today')
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0])
  const [lastUpdate, setLastUpdate] = useState(new Date())

  useEffect(() => {
    loadGames()
    // Refresh data every 30 seconds for live games
    const interval = setInterval(() => {
      if (selectedTab === 'today') {
        loadGames()
      }
    }, 30000)
    return () => clearInterval(interval)
  }, [selectedTab, selectedDate])

  const loadGames = async () => {
    try {
      setLoading(true)
      setError(null)

      if (selectedTab === 'today') {
        // Get today's games with real data
        const date = new Date(selectedDate)
        const comprehensiveData = await sportsDataService.getComprehensiveNBAData(date)

        setGames(comprehensiveData.todaysGames)
        setRecentGames(comprehensiveData.recentGames.slice(0, 20)) // Last 20 games
        setUpcomingGames(comprehensiveData.upcomingGames.slice(0, 20)) // Next 20 games
        setOdds(comprehensiveData.odds)

        // Add predictions to games
        const gamesWithPredictions = await Promise.all(
          comprehensiveData.todaysGames.map(async game => {
            const predictions = game.homeTeam.stats && game.awayTeam.stats
              ? await advancedAnalytics.calculateAdvancedWinProbability(
                  game.homeTeam,
                  game.awayTeam
                )
              : null

            return { ...game, predictions }
          })
        )

        setGames(gamesWithPredictions)
      } else if (selectedTab === 'recent') {
        const recent = await sportsDataService.getRecentNBAGames()
        setRecentGames(recent)
      } else if (selectedTab === 'upcoming') {
        const upcoming = await sportsDataService.getUpcomingNBAGames()
        setUpcomingGames(upcoming)
      }

      setLastUpdate(new Date())
    } catch (err) {
      console.error('Error loading games:', err)
      setError('Failed to load NBA games. The API might be temporarily unavailable.')
      // Don't use mock data - show the error
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (game) => {
    const status = game.status?.toLowerCase()

    // Only show LIVE if the game is actually in progress
    if (status === 'live' || status === 'in_progress') {
      const periodText = game.period && game.clock ? `Q${game.period} ${game.clock}` : ''
      return (
        <span className="flex items-center gap-1 text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded animate-pulse">
          <span className="w-2 h-2 bg-red-500 rounded-full"></span>
          LIVE {periodText}
        </span>
      )
    } else if (status === 'final' || status === 'ended') {
      return (
        <span className="text-xs bg-gray-700 text-gray-400 px-2 py-1 rounded">
          FINAL
        </span>
      )
    }

    // For scheduled games, show the time
    const gameDate = new Date(game.date)
    const timeStr = game.clock || gameDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/New_York'
    })

    return (
      <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">
        {timeStr} ET
      </span>
    )
  }

  const formatOdds = (price) => {
    if (!price) return 'N/A'
    return price > 0 ? `+${price}` : price.toString()
  }

  const refreshGames = () => {
    loadGames()
  }

  const getDisplayGames = () => {
    switch (selectedTab) {
      case 'recent':
        return recentGames
      case 'upcoming':
        return upcomingGames
      default:
        return games
    }
  }

  const displayGames = getDisplayGames()

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-3xl font-bold text-white">NBA Games & Live Odds</h1>

        <div className="flex gap-3 mt-4 sm:mt-0">
          {selectedTab === 'today' && (
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-2 bg-dark-card border border-dark-border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          )}

          <button
            onClick={refreshGames}
            className="p-2 bg-dark-card border border-dark-border rounded-lg text-white hover:bg-gray-700 transition-colors"
            title="Refresh games"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tabs for Recent/Today/Upcoming */}
      <div className="flex gap-2 border-b border-dark-border">
        <button
          onClick={() => setSelectedTab('recent')}
          className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${
            selectedTab === 'recent'
              ? 'text-primary-500 border-b-2 border-primary-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Trophy className="h-4 w-4" />
          Recent Games
        </button>
        <button
          onClick={() => setSelectedTab('today')}
          className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${
            selectedTab === 'today'
              ? 'text-primary-500 border-b-2 border-primary-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Calendar className="h-4 w-4" />
          Today's Games
        </button>
        <button
          onClick={() => setSelectedTab('upcoming')}
          className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${
            selectedTab === 'upcoming'
              ? 'text-primary-500 border-b-2 border-primary-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <TrendingUp className="h-4 w-4" />
          Upcoming Games
        </button>
      </div>

      {/* Last Update */}
      <div className="text-sm text-gray-400">
        Last updated: {lastUpdate.toLocaleTimeString()} • {selectedTab === 'today' && 'Updates every 30 seconds'}
        <span className="ml-2">• Data from ESPN API (100% Free)</span>
      </div>

      {/* Live Games Alert */}
      {selectedTab === 'today' && displayGames.some(g => g.status === 'live' || g.status === 'in_progress') && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <span className="flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
            </span>
            <p className="text-red-400 font-medium">
              {displayGames.filter(g => g.status === 'live' || g.status === 'in_progress').length} NBA games currently live!
            </p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-400" />
            <p className="text-yellow-400">{error}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && displayGames.length === 0 ? (
        <div className="flex justify-center items-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent mx-auto"></div>
            <p className="mt-4 text-gray-400">Loading real NBA games...</p>
          </div>
        </div>
      ) : displayGames.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-gray-400 text-lg">
            {selectedTab === 'today' ? 'No NBA games scheduled for this date' : `No ${selectedTab} games found`}
          </p>
          {selectedTab === 'today' && (
            <p className="text-gray-500 text-sm mt-2">Try selecting a different date or check recent/upcoming games</p>
          )}
        </div>
      ) : (
        /* Games Grid */
        <div className="grid grid-cols-1 gap-4">
          {displayGames.map((game) => {
            // Find odds for this game
            const gameOdds = odds.find(o =>
              o.id === game.id ||
              (o.home_team?.includes(game.homeTeam.name) && o.away_team?.includes(game.awayTeam.name))
            )

            const spread = gameOdds?.bookmakers?.[0]?.markets?.find(m => m.key === 'spreads')
            const total = gameOdds?.bookmakers?.[0]?.markets?.find(m => m.key === 'totals')
            const moneyline = gameOdds?.bookmakers?.[0]?.markets?.find(m => m.key === 'h2h')

            const gameDate = new Date(game.date)
            const dateStr = gameDate.toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric'
            })

            return (
              <div
                key={game.id}
                className="bg-dark-card border border-dark-border rounded-lg p-6 hover:border-primary-500/50 transition-colors"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {getStatusBadge(game)}
                    <span className="text-sm text-gray-500">{dateStr}</span>
                    {game.series && (
                      <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded">
                        {game.series}
                      </span>
                    )}
                    {game.broadcast && (
                      <span className="text-xs text-gray-400">📺 {game.broadcast}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {game.venue && (
                      <span className="text-sm text-gray-400">📍 {game.venue}</span>
                    )}
                    {game.predictions && (
                      <span className="text-sm text-gray-400">
                        AI: {game.predictions.confidence?.toFixed(0) || 'N/A'}%
                      </span>
                    )}
                  </div>
                </div>

                {/* Teams */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="md:text-right">
                    <div className="flex items-center gap-2 md:justify-end">
                      {game.awayTeam.logo ? (
                        <img src={game.awayTeam.logo} alt={game.awayTeam.name} className="h-8 w-8" />
                      ) : (
                        <span className="text-2xl">🏀</span>
                      )}
                      <div>
                        <p className="font-bold text-white">
                          {game.awayTeam.name}
                        </p>
                        <p className="text-xs text-gray-400">
                          {game.awayTeam.record || (game.awayTeam.stats && `${game.awayTeam.stats.wins}-${game.awayTeam.stats.losses}`)}
                        </p>
                        {game.awayTeam.stats?.streak && (
                          <p className="text-sm text-gray-400">
                            Streak: {game.awayTeam.stats.streak}
                          </p>
                        )}
                      </div>
                    </div>
                    {game.awayTeam.score !== null && (
                      <p className="text-2xl font-bold text-white mt-2 md:text-right">{game.awayTeam.score}</p>
                    )}
                  </div>

                  <div className="text-center flex items-center justify-center">
                    <span className="text-gray-500 text-lg font-medium">@</span>
                  </div>

                  <div>
                    <div className="flex items-center gap-2">
                      {game.homeTeam.logo ? (
                        <img src={game.homeTeam.logo} alt={game.homeTeam.name} className="h-8 w-8" />
                      ) : (
                        <span className="text-2xl">🏀</span>
                      )}
                      <div>
                        <p className="font-bold text-white">
                          {game.homeTeam.name}
                        </p>
                        <p className="text-xs text-gray-400">
                          {game.homeTeam.record || (game.homeTeam.stats && `${game.homeTeam.stats.wins}-${game.homeTeam.stats.losses}`)}
                        </p>
                        {game.homeTeam.stats?.streak && (
                          <p className="text-sm text-gray-400">
                            Streak: {game.homeTeam.stats.streak}
                          </p>
                        )}
                      </div>
                    </div>
                    {game.homeTeam.score !== null && (
                      <p className="text-2xl font-bold text-white mt-2">{game.homeTeam.score}</p>
                    )}
                  </div>
                </div>

                {/* Team Stats Comparison */}
                {game.homeTeam.stats && game.awayTeam.stats && (
                  <div className="grid grid-cols-3 gap-2 mb-4 text-sm">
                    <div className="bg-gray-800/30 rounded p-2 text-center">
                      <p className="text-gray-500 text-xs">Win %</p>
                      <p className="text-white">{game.awayTeam.stats.winPct || '0'}% | {game.homeTeam.stats.winPct || '0'}%</p>
                    </div>
                    <div className="bg-gray-800/30 rounded p-2 text-center">
                      <p className="text-gray-500 text-xs">Home/Away</p>
                      <p className="text-white text-xs">{game.awayTeam.stats.awayRecord || 'N/A'} | {game.homeTeam.stats.homeRecord || 'N/A'}</p>
                    </div>
                    <div className="bg-gray-800/30 rounded p-2 text-center">
                      <p className="text-gray-500 text-xs">Last 10</p>
                      <p className="text-white">{game.awayTeam.stats.last10 || 'N/A'} | {game.homeTeam.stats.last10 || 'N/A'}</p>
                    </div>
                  </div>
                )}

                {/* Betting Lines (only for today's/upcoming games) */}
                {(selectedTab === 'today' || selectedTab === 'upcoming') && !game.homeTeam.score && (
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-gray-800/50 rounded-lg p-3">
                      <p className="text-xs text-gray-400 mb-1">Spread</p>
                      {spread ? (
                        <>
                          <p className="text-sm font-medium text-white">
                            {game.awayTeam.abbreviation} {spread.outcomes[1]?.point > 0 ? '+' : ''}{spread.outcomes[1]?.point}
                          </p>
                          <p className="text-sm font-medium text-white">
                            {game.homeTeam.abbreviation} {spread.outcomes[0]?.point > 0 ? '+' : ''}{spread.outcomes[0]?.point}
                          </p>
                        </>
                      ) : (
                        <p className="text-sm text-gray-500">Coming soon</p>
                      )}
                    </div>

                    <div className="bg-gray-800/50 rounded-lg p-3">
                      <p className="text-xs text-gray-400 mb-1">Total</p>
                      {total ? (
                        <>
                          <p className="text-sm font-medium text-white">O {total.outcomes[0]?.point}</p>
                          <p className="text-sm font-medium text-white">U {total.outcomes[1]?.point}</p>
                        </>
                      ) : (
                        <p className="text-sm text-gray-500">Coming soon</p>
                      )}
                    </div>

                    <div className="bg-gray-800/50 rounded-lg p-3">
                      <p className="text-xs text-gray-400 mb-1">Moneyline</p>
                      {moneyline ? (
                        <>
                          <p className="text-sm font-medium text-white">
                            {formatOdds(moneyline.outcomes.find(o => o.name?.includes(game.awayTeam.name))?.price)}
                          </p>
                          <p className="text-sm font-medium text-white">
                            {formatOdds(moneyline.outcomes.find(o => o.name?.includes(game.homeTeam.name))?.price)}
                          </p>
                        </>
                      ) : (
                        <p className="text-sm text-gray-500">Coming soon</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Button */}
                {!game.homeTeam.score && (
                  <div className="mt-4">
                    <Link
                      to={`/game/${game.id}`}
                      className="block w-full bg-primary-500 hover:bg-primary-600 text-white font-medium py-2 px-4 rounded-lg transition-colors text-center flex items-center justify-center gap-2"
                    >
                      <BarChart3 className="h-4 w-4" />
                      View Full Analytics
                    </Link>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Data Source Attribution */}
      <div className="text-center text-xs text-gray-500 mt-8">
        Real-time NBA data from ESPN API • 100% Free • No API Key Required
      </div>
    </div>
  )
}

export default Games